// AdminOverlay.js - Admin Panel come overlay minimizzabile con Basic Auth
import React, { useState, useEffect } from "react"
import { FaExpand, FaTimes } from "react-icons/fa"
import { FaMinimize } from "react-icons/fa6"
import AdminPanel from "./AdminPanel"
import { withBasicAuth, useBasicAuth } from "./ImprovedBasicAuth" // MODIFICATO: Basic Auth

const AdminOverlay = ({
  selectedProductCode,
  onProductChange,
  isVisible,
  onClose,
  onLogout: externalLogout, // NUOVO: Logout passato dal HOC
}) => {
  const [isMinimized, setIsMinimized] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [position, setPosition] = useState({ x: 20, y: 20 })
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [activeTab, setActiveTab] = useState("products")

  // MODIFICATO: Hook per Basic Auth
  const { logout } = useBasicAuth()

  // Esponi la funzione setActiveTab globally per AdminPanel
  useEffect(() => {
    window.adminOverlayRef = {
      setActiveTab,
    }

    return () => {
      delete window.adminOverlayRef
    }
  }, [])

  // Gestione ESC per chiudere l'overlay
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27 && isVisible) {
        onClose()
      }
    }
    document.addEventListener("keydown", handleEsc)
    return () => document.removeEventListener("keydown", handleEsc)
  }, [isVisible, onClose])

  // Gestione drag per la barra minimizzata
  const handleMouseDown = (e) => {
    if (!isMinimized) return
    setIsDragging(true)
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    })
  }

  const handleMouseMove = (e) => {
    if (!isDragging || !isMinimized) return
    const newX = e.clientX - dragStart.x
    const newY = e.clientY - dragStart.y

    // Limiti dello schermo
    const maxX = window.innerWidth - 300 // larghezza barra minimizzata
    const maxY = window.innerHeight - 50 // altezza barra minimizzata

    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    })
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
      return () => {
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("mouseup", handleMouseUp)
      }
    }
  }, [isDragging, dragStart, position])

  // MODIFICATO: Gestione logout con Basic Auth
  const handleLogout = () => {
    if (confirm("Sei sicuro di voler uscire dall'area admin?")) {
      logout()
      if (externalLogout) {
        externalLogout()
      }
    }
  }

  // Determina la larghezza del container basata sul tab attivo
  const getContainerWidth = () => {
    if (activeTab === "views") {
      return "ay-w-[33vw] ay-min-w-96"
    }
    return "ay-w-[95vw]"
  }

  // Determina la posizione del container per ViewsTab
  const getContainerPosition = () => {
    if (activeTab === "views") {
      return "ay-right-0 ay-top-0 ay-h-full"
    }
    return "ay-top-[2.5vh] ay-left-[2.5vw] ay-h-[95vh] ay-rounded-lg"
  }

  if (!isVisible) return null

  return (
    <>
      {/* Backdrop oscuro quando espanso - SOLO per modalità non-Views */}
      {!isMinimized && activeTab !== "views" && (
        <div
          className="ay-fixed ay-inset-0 ay-bg-black ay-bg-opacity-30 ay-z-40 ay-transition-opacity ay-duration-300"
          onClick={onClose}
        />
      )}

      {/* Admin Panel Container */}
      <div
        className={`ay-fixed ay-z-50 ay-transition-all ay-duration-300 ay-ease-in-out ay-shadow-2xl ${
          isMinimized
            ? "ay-w-80 ay-h-12 ay-rounded-md ay-shadow-lg ay-cursor-move"
            : `${getContainerWidth()} ${getContainerPosition()}`
        }`}
        style={
          isMinimized
            ? {
                left: `${position.x}px`,
                top: `${position.y}px`,
              }
            : {}
        }
      >
        {isMinimized ? (
          // Barra minimizzata
          <div
            className="ay-w-full ay-h-full ay-bg-white ay-border ay-border-gray-300 ay-rounded-md ay-flex ay-items-center ay-justify-between ay-px-4 ay-select-none"
            onMouseDown={handleMouseDown}
          >
            <div className="ay-flex ay-items-center ay-gap-2">
              <div className="ay-w-2 ay-h-2 ay-bg-blue-500 ay-rounded-full"></div>
              <span className="ay-text-sm ay-font-medium ay-text-gray-700">
                Admin Panel
              </span>
              {selectedProductCode && (
                <span className="ay-text-xs ay-text-gray-500">
                  ({selectedProductCode})
                </span>
              )}
              {activeTab === "views" && (
                <span className="ay-text-xs ay-bg-purple-100 ay-text-purple-800 ay-px-2 ay-py-1 ay-rounded">
                  Custom Views
                </span>
              )}
            </div>
            <div className="ay-flex ay-items-center ay-gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setIsMinimized(false)
                }}
                className="ay-p-1 ay-text-gray-500 ay-hover:ay-text-gray-700 ay-hover:ay-bg-gray-100 ay-rounded ay-transition-colors"
                title="Espandi"
              >
                <FaExpand size={12} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onClose()
                }}
                className="ay-p-1 ay-text-gray-500 ay-hover:ay-text-red-600 ay-hover:ay-bg-red-50 ay-rounded ay-transition-colors"
                title="Chiudi"
              >
                <FaTimes size={12} />
              </button>
            </div>
          </div>
        ) : (
          // Panel espanso
          <div
            className={`ay-w-full ay-h-full ay-bg-white ay-overflow-hidden ay-flex ay-flex-col ${
              activeTab === "views" ? "" : "ay-rounded-lg"
            }`}
          >
            {/* Header personalizzato per overlay */}
            <div
              className={`ay-flex ay-items-center ay-justify-between ay-p-4 ay-bg-gray-50 ay-border-b ay-border-gray-200 ay-flex-shrink-0 ${
                activeTab === "views" ? "" : "ay-rounded-t-lg"
              }`}
            >
              <div className="ay-flex ay-items-center ay-gap-3">
                <div
                  className={`ay-w-3 ay-h-3 ay-rounded-full ${
                    activeTab === "views"
                      ? "ay-bg-purple-500"
                      : "ay-bg-blue-500"
                  }`}
                ></div>
                <div>
                  <h2 className="ay-text-lg ay-font-semibold ay-text-gray-800">
                    Admin Panel
                    {activeTab === "views" && (
                      <span className="ay-ml-2 ay-text-sm ay-bg-purple-100 ay-text-purple-800 ay-px-2 ay-py-1 ay-rounded">
                        Custom Views Mode
                      </span>
                    )}
                  </h2>
                  <p className="ay-text-xs ay-text-gray-500">
                    {activeTab === "views"
                      ? "Modalità Custom Views - Gestisci viste camera personalizzate"
                      : "Overlay Mode - Gestione Configuratore 3D"}
                  </p>
                </div>
              </div>
              <div className="ay-flex ay-items-center ay-gap-2">
                {/* Pulsante logout */}
                <button
                  onClick={handleLogout}
                  className="ay-p-2 ay-text-gray-500 ay-hover:ay-text-orange-600 ay-hover:ay-bg-orange-50 ay-rounded ay-transition-colors"
                  title="Logout dall'area admin"
                >
                  <svg
                    className="ay-w-4 ay-h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => setIsMinimized(true)}
                  className="ay-p-2 ay-text-gray-500 ay-hover:ay-text-gray-700 ay-hover:ay-bg-gray-200 ay-rounded ay-transition-colors"
                  title="Minimizza"
                >
                  <FaMinimize size={14} />
                </button>
                <button
                  onClick={onClose}
                  className="ay-p-2 ay-text-gray-500 ay-hover:ay-text-red-600 ay-hover:ay-bg-red-50 ay-rounded ay-transition-colors"
                  title="Chiudi (ESC)"
                >
                  <FaTimes size={16} />
                </button>
              </div>
            </div>

            {/* Contenuto Admin Panel - usa il componente esistente */}
            <div className="ay-flex-1 ay-overflow-hidden">
              <AdminPanelWrapper
                selectedProductCode={selectedProductCode}
                onProductChange={onProductChange}
                activeTab={activeTab}
                onLogout={externalLogout}
              />
            </div>
          </div>
        )}
      </div>
    </>
  )
}

// Wrapper component per AdminPanel che nasconde l'header quando usato nell'overlay
const AdminPanelWrapper = ({
  selectedProductCode,
  onProductChange,
  activeTab,
  onLogout,
}) => {
  return (
    <div
      className="ay-h-full ay-overlay-admin-panel"
      style={{
        // Stili CSS inline per nascondere elementi quando in modalità overlay
        "--admin-header-display": "none",
        "--min-height": "100%",
        "--bg-color": "transparent",
      }}
    >
      <AdminPanel
        selectedProductCode={selectedProductCode}
        onProductChange={onProductChange}
        isOverlay={true}
        activeTab={activeTab}
        onLogout={onLogout}
      />
    </div>
  )
}

// MODIFICATO: Esporta il componente protetto con Basic Auth
export default withBasicAuth(AdminOverlay)
